import { get } from 'lodash';
import * as R from 'remeda';
import { createSelector } from 'reselect';
import { isEmbedded } from '../../../constants/config';
import {
  assertMutable,
  sidebarFooterActions,
  SidebarFooterActionsOrder,
} from '../../../features/annotation-view/document-sidebar/sidebar-footer/sidebar-footer-actions/utils';
import createDeepEqualSelector from '../../../lib/createDeepEqualSelector';
import { Url } from '../../../types/basic';
import { GroupRole } from '../../../types/group';
import { ProductTourName } from '../../../types/productTour';
import { State } from '../../../types/state';
import {
  AnnotationValidation,
  FooterLayout,
  SortFooterColumns,
} from '../../../types/user';
import {
  canManageQueue,
  getRoleName,
  isApproverRole,
  roleNames,
} from '../groups/helpers';
import { organizationSelector } from '../organization/selectors';
import { complexLineItemsEnabledSelector } from '../ui/selectors';

export const userSelector = (state: State) => state.user;
export const groupSelector = (state: State) => state.groups;
export const firstTimeUserSelector = (state: State) =>
  R.isDeepEqual(state.user.uiSettings.hints, {}) &&
  !state.user.uiSettings.onboardingAcknowledged;

export const displayExtensionsIntroBannerSelector = (state: State) =>
  !state.user.uiSettings.hideExtensionsIntroBanner;

export const userHookStateSelector = (state: State) =>
  state.user.uiSettings.hookState ?? {};

export const displayDAGIntroBannerSelector = (state: State) =>
  !state.user.uiSettings.hideDagIntroBanner;

export const allDocumentsDashboardCustomizationSelector = (state: State) =>
  state.user.uiSettings.documentsDashboard;

export const confidenceIndicatorEnabledSelector = (state: State) =>
  state.user.uiSettings.showConfidenceScore;

export const userRoleNameSelector = createDeepEqualSelector(
  userSelector,
  groupSelector,
  (user, groups) => getRoleName(get(user, 'groups'), groups)
);

export const isUserViewer = (state: State) =>
  isUserOfRoleSelector(state)('viewer') || isUserRoleUnknownSelector(state);

export const isUserAdmin = (state: State) =>
  isUserOfRoleSelector(state)('admin') ||
  isUserOfRoleSelector(state)('organization_group_admin');

export const isUserAnnotatorBetaSelector = (state: State) =>
  isUserOfRoleSelector(state)('annotator_limited');

export const isEmpoweredManager = (state: State) =>
  isUserOfRoleSelector(state)('empowered_manager');

export const isUserAtLeastManager = (state: State) =>
  isUserOfRoleSelector(state)('manager') ||
  isUserOfRoleSelector(state)('empowered_manager') ||
  isUserOfRoleSelector(state)('admin') ||
  isUserOfRoleSelector(state)('organization_group_admin');

export const canManageQueueSelector = (
  state: State,
  queueUrl: Url | undefined
): boolean =>
  !!queueUrl &&
  canManageQueue({
    isAdminLike: isUserAdmin(state),
    isEmpoweredManager: isEmpoweredManager(state),
    userQueueUrls: state.user.queues,
    queueUrl,
  });

export const isUserOrgGroupAdminSelector = (state: State) =>
  isUserOfRoleSelector(state)('organization_group_admin');

export const isUserOfRoleSelector = createSelector(
  userSelector,
  groupSelector,
  (user, groups) => (desiredGroup: GroupRole) =>
    groups
      .filter(group => user.groups.includes(group.url))
      .some(userGroup => userGroup.name === desiredGroup)
);

const isUserRoleUnknownSelector = createSelector(
  userRoleNameSelector,
  (roleName: GroupRole) => !roleNames.includes(roleName)
);

export const tourResultSelector =
  (tourName: ProductTourName) => (state: State) =>
    state.user.uiSettings?.productTours?.[tourName]?.result ?? null;

// support access or membership access - user from different organization
export const isUserInDifferentOrganization = createSelector(
  userSelector,
  organizationSelector,
  (user, organization) => {
    return user.organization !== organization.url;
  }
);

export const canUserPurgeSelector = createSelector(
  isUserAdmin,
  isAdmin => isAdmin
);

export const canUserApproveSelector = createSelector(
  isUserAdmin,
  userSelector,
  groupSelector,
  (isAdmin, user, group) => isApproverRole(user.groups, group) || isAdmin
);

export const footerLayoutSelector = (state: State): FooterLayout => {
  const value = state.user?.uiSettings.footerLayout;
  return value ?? 'table-per-page';
};

// Accounts created on/after this date default CLI users to schema-sorted ("Queue
// settings"). Set to the deploy date. To make the change blanket (all CLI users
// regardless of account age), remove the dateJoined check in the selector below.
export const QUEUE_SETTINGS_DEFAULT_CUTOFF = new Date('2026-07-01T00:00:00Z');

export const sortFooterColumnsSelector = createSelector(
  (state: State) => state.user?.uiSettings.sortFooterColumns,
  footerLayoutSelector,
  complexLineItemsEnabledSelector,
  (state: State) => state.user?.dateJoined,
  (sorting, layout, complexLineItemsEnabled, dateJoined): SortFooterColumns => {
    // explicit user preference always wins
    if (sorting !== undefined) {
      return sorting;
    }

    // new CLI accounts default to schema-sorted ("Queue settings")
    if (
      complexLineItemsEnabled &&
      new Date(dateJoined) >= QUEUE_SETTINGS_DEFAULT_CUTOFF
    ) {
      return 'queue-settings';
    }

    // legacy behavior: derive the default from the footer layout preference
    return layout === 'table-per-page' ? 'automatically' : 'queue-settings';
  }
);

export const sidebarAdditionalInfoSelector = (state: State) =>
  isEmbedded() ? undefined : state.user.uiSettings.sidebarAdditionalInfo;

export const onConfirmNavigateSelector = (state: State) =>
  state.user.uiSettings.sidebarNavigation?.onConfirm ??
  'sidebarNavigation.next';

export const sidebarFieldIdsEnabledSelector = createSelector(
  sidebarAdditionalInfoSelector,
  info => info?.includes('sidebarAdditionalInfo.fieldIds')
);

export const sidebarConfidenceScoresEnabledSelector = createSelector(
  sidebarAdditionalInfoSelector,
  info => info?.includes('sidebarAdditionalInfo.confidenceScores')
);

export const sidebarHiddenFieldsVisibleSelector = createSelector(
  sidebarAdditionalInfoSelector,
  info => {
    return info?.includes('sidebarAdditionalInfo.hiddenFieldsVisible');
  }
);

export const sidebarAutomationBlockersByDefaultVisibleSelector = createSelector(
  sidebarAdditionalInfoSelector,
  info => info?.includes('sidebarAdditionalInfo.automationBlockers')
);

const assertAllActionsIncluded = (
  actionsOrder: SidebarFooterActionsOrder | undefined
): SidebarFooterActionsOrder => {
  if (!actionsOrder) {
    return assertMutable(sidebarFooterActions);
  }

  // invalid actions can appear e.g. from review apps or during rollbacks
  const validActionsOrder = R.intersection(actionsOrder, sidebarFooterActions);
  const notIncluded = R.difference(sidebarFooterActions, validActionsOrder);
  return [...validActionsOrder, ...notIncluded] as SidebarFooterActionsOrder;
};

export const sidebarActionsOrderSelector = (state: State) =>
  isEmbedded()
    ? undefined
    : assertAllActionsIncluded(state.user.uiSettings.sidebarActionsOrder);

export const annotationValidationSelector = (
  state: State
): AnnotationValidation =>
  state.user?.uiSettings.annotationValidation ?? 'automatically';

export const userLabConfigSelector = (state: State) =>
  state.user.uiSettings.labConfig ?? {};
